import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./TeaserBanner.module.scss";
import TeaserBannerContent from "./TeaserBannerContent.model";

import { useInView } from "react-intersection-observer";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import ImageViewer from "components/ImageViewer/ImageViewer";
import isBrowserContext from "helpers/isBrowserContext";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";
import Badge from "./components/BadgeSVG/BadgeSVG";
import BlockButton from "components/BlockButton/BlockButton";
import { useSelector } from "react-redux";
import SimpleImage from "app/blocks/SimpleImage/SimpleImage";

const TeaserBanner: FunctionComponent<TeaserBannerContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.headline)));
    const processedBodyText = getProcessedRichtext(props.content.text);
    const wrapperStyles = getWrapperStyles(props.settings);
    const processedTagline = getProcessedRichtext(formatDynamicTextBreak(props.content.tagline));

    const imageCrop = getImageCrop(props.content.media?.crops, props.content.mediaCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.media?.width, props.content.media?.height);

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;

    const bgColor = props.settings?.backgroundColor?.color || "#d8e8c5";
    const buttonHoverBackgroundColor = props.settings?.buttonHoverBackgroundColor?.color || "";
    const buttonHoverTextColor = props.settings?.buttonHoverColor?.color || "";
    const badgeBgColor = props.settings?.badgeBackgroundColor?.color || "#d8e8c5";
    const badgeTextColor = props.settings?.badgeTextColor?.color || "#000000";
    const maskColor = `${props.settings?.maskColor?.color || "#FFFFFF"}`;
    function getBottomPosition() {
        if (props.settings?.spacingBottom === "Large") return "15%";
        if (props.settings?.spacingBottom === "Medium") return "10%";
        if (props.settings?.spacingBottom === "XLarge") return "20%";
    }

    const blocks = useSelector((state: any) => state.page?.content?.blocks);

    // Build array of teaser banners and the block background below them
    const teaserBannerWithBelowBg = blocks
        .map((block: any, index: number) => {
            if (block.content?._contentType !== "blockTeaserBanner") return null;
            const nextBlock = blocks[index + 1];
            const bg = nextBlock?.settings?.backgroundColor;
            const nextBgColor = bg?.color || bg?.value || null;

            return {
                index,
                teaserBanner: block,
                nextBlock,
                nextBgColor,
            };
        })
        .filter(Boolean);

    // Find the entry for the current teaser banner
    const currentTeaserBanner = teaserBannerWithBelowBg.find((item: any) => item.teaserBanner.contentUdi === props.contentUdi);
    const nextBlockBgColor = currentTeaserBanner?.nextBgColor ? `#${currentTeaserBanner.nextBgColor.replace("#", "")}` : "white";

    return (
        <div
            className={combineClasses([styles.wrapper, ...wrapperStyles])}
            style={
                {
                    "--btn-hover-bg": `${buttonHoverBackgroundColor}`,
                    "--btn-hover-color": `${buttonHoverTextColor}`,
                    backgroundColor: maskColor,
                } as React.CSSProperties
            }
        >
            <div
                className={combineClasses([styles.inner])}
                style={{ backgroundColor: `${bgColor}` }}
            >
                <div
                    className={combineClasses([styles.content, isBrowserClass, inViewClass])}
                    ref={ref}
                >
                    <div className={styles.text_content}>
                        <span
                            className={styles.tagline}
                            dangerouslySetInnerHTML={processedTagline}
                        />
                        {props.content.headline ? (
                            <DynamicHeading
                                className={styles.headline}
                                headlineSize={props.content.headlineSize}
                                headlineWeight={props.content.headlineWeight}
                                innerHtml={processedHeadline}
                            />
                        ) : null}

                        {processedBodyText ? (
                            <div
                                className={styles.bodyText}
                                dangerouslySetInnerHTML={processedBodyText}
                            ></div>
                        ) : null}

                        {props.content && props.content.buttons && props.content.buttons?.length > 0 && (
                            <div className={styles.buttons}>
                                {props.content.buttons.map((button) => (
                                    <BlockButton
                                        key={button.contentUdi}
                                        label={button?.label}
                                        link={button?.link}
                                        target={button?.target}
                                        borderColor={button?.borderColor}
                                        backgroundColor={button?.backgroundColor}
                                        textColor={button?.textColor}
                                        backgroundHoverColor={button?.backgroundHoverColor}
                                        hoverTextColor={button?.hoverTextColor}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {/*  TODO: get path to the image, remove  */}

                {props.content.media && (
                    <div className={styles.media}>
                        <SimpleImage
                            className={styles.img}
                            src={props.content.media}
                        />
                        {/* <ImageViewer
                            overwriteImageStyles={styles.img}
                            className={styles.image}
                            src={props.content.media?.url}
                            crop={imageCrop}
                            type="containX"
                            focalPoint={props.content.media.focalPoint}
                            {...imageAspectRatio}
                            alt={props.content.mediaAlternate}
                        /> */}
                    </div>
                )}
            </div>

            {props.content.badgeToggle && props.content.badgeText && (
                <div
                    className={styles.badge_wrapper}
                    style={{ bottom: `${getBottomPosition()}` }}
                >
                    <Badge
                        text={props.content.badgeText}
                        backgroundColor={badgeBgColor}
                        color={badgeTextColor}
                    />
                </div>
            )}
        </div>
    );
};

export default TeaserBanner;
